<script lang="ts">
    import CN from "classnames";

    import BaseButton from "@components/Buttons/BaseButton.svelte";
    import Spinner from "@components/Spinner/Spinner.svelte";

    let className = "";
    export { className as class };

    export let variant:
        | "contained"
        | "outlined"
        | "text"
        | "link"
        | "opacityOnHover" = "contained";
    export let size: "small" | "medium" | "large" = "medium";
    export let upperCase = false;
    export let loading = false;
    export let rounded = false;
    export let squared = false;
    export let url: string | null = null;
    export let target: "_self" | "_parent" | "_top" | "_blank" | undefined =
        undefined;
    export let rel: string | undefined = undefined;

    $: classes = CN(
        "gw-button",
        {
            "--contained": variant === "contained",
            "--outlined": variant === "outlined",
            "--text": variant === "text",
            "--link": variant === "link",
            "--opacityOnHover": variant === "opacityOnHover",
        },
        {
            "--small": size === "small",
            "--medium": size === "medium",
            "--large": size === "large",
        },
        {
            "--upperCase": upperCase,
            "--rounded": rounded,
            "--squared": squared,
        },
        {
            "--loading": loading,
        },
        className
    );

    $: spinnerColor =
        variant === "opacityOnHover" ? "var(--gw-button__main-color)" : "#ccc";
</script>

<BaseButton
    {...$$props}
    {url}
    {target}
    {rel}
    class={classes}
    on:click
    on:mousedown
    on:mouseup
>
    <div
        class={CN("gw-button__loading", {
            "gw-button__loading--show": loading,
        })}
    >
        <Spinner color={spinnerColor} size={"18px"} />
    </div>
    {#if $$slots.icon}
        <span class="gw-button__icon">
            <slot name="icon" />
        </span>
    {/if}
    <slot />
</BaseButton>

<style lang="postcss" global>
    .gw-button .gw-button__loading {
        height: 100%;
        width: 100%;
        position: absolute;
        left: 0;
        top: 0;
        justify-content: center;
        align-items: center;
        display: none;
    }

    .gw-button .gw-button__loading.gw-button__loading--show {
        display: flex;
    }

    .gw-btn.gw-button {
        box-sizing: border-box;
        display: inline-flex;
        line-height: 1em;
        align-items: center;
        justify-content: center;
        font-weight: 500;
        position: relative;
        overflow: hidden;
        transition: all var(--gw-button-transition-duration) ease;
        gap: 0.5em;
        padding: 0.5em 0.875em;
        text-decoration: none;
        cursor: pointer;

        &.--contained {
            background: var(--gw-button__main-color);
            color: var(--gw-button__text-color);
            border: 1px solid transparent;
        }

        &.--outlined {
            border: 1px solid var(--gw-button__main-color);
            color: var(--gw-button__text-color);
            background-color: transparent;

            &:hover,
            &:focus,
            &:focus-within {
                /* background: var(--gw-button__main-color); */
            }
        }

        &.--opacityOnHover {
            --gw-spinner-color: var(--gw-button__main-color);

            opacity: 1;
            border: 1px solid var(--gw-button__main-color);
            color: var(--gw-button__text-color);
            background-color: transparent;

            &:hover,
            &:focus,
            &:focus-within {
                opacity: 0.8;
                background: none;
            }
        }

        &.--text {
            background: none;
            border: none;

            &:hover,
            &:focus,
            &:focus-within {
                background: var(--gw-button-text-bg, #fafafa);
            }
        }

        &.--small {
            font-size: var(--gw-font-size-xxs);
            padding: 0.5em 0.875em;
            border-radius: var(--gw-button-border-radius-sm, 2px);
        }

        &.--medium {
            font-size: var(--gw-font-size-xs);
            padding: 0.625em 1em;
            border-radius: var(--gw-button-border-radius-md, 2px);
        }

        &.--large {
            font-size: var(--gw-font-size-sm);
            padding: 0.75em 1.125em;
            border-radius: var(--gw-button-border-radius-lg, 2px);
        }

        &.--link {
            text-decoration-line: underline;
            padding: 0;
        }

        &.--upperCase {
            text-transform: uppercase;
        }

        &__icon {
            display: flex;
        }

        &.--rounded {
            border-radius: 9999px;
        }

        &.--squared {
            border-radius: 0;
        }

        &.--loading {
            color: transparent;
        }

        &:disabled {
            pointer-events: none;
            opacity: 0.6;
        }
    }
</style>
